// FeatureProgressPage.jsx — 功能代码妥善率表的三态记录（SB-201 S3，第三百零八批；需求方指令「类似资产对账的 tab」）。
//
// ── 它与功能表页的分工 ──────────────────────────────────────────────────────
// 功能表页（文档面）是正典的呈现：主表与 §2 派生表由仓内 md／jsonl 生成。这一页是应用面：同一份行
// （按启用版本分组），每一格可以**记**——记的是三态事件（实现／自测／QA），落进协调系统的追加式台账，
// 工作空间按批拉回正典，功能表五格与派生表随之更新。没有"改状态"，只有"追加一条带证据的记录"。
//
// ── 谁能记什么，由服务端说 ──────────────────────────────────────────────────
// GET /api/feature-states 回 `role`（岗位派生：program／qa／admin；空串＝只读）。按钮显隐照它，真正的闸在 POST（403）。
//
// ── 证据锚定 git 提交 ────────────────────────────────────────────────────────
// 需求方 2026-09-24 指令：证据建议为 git 提交。done／passed 必填证据；填的不像提交（无 sha／PR）时给一行提示、不拦。
//
// ── 缺省全部收起 ────────────────────────────────────────────────────────────
// 与资产对账页同一条裁定：先看进度、再点开要记的那一版；收起的版本不挂载。
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Divider, MenuItem, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import HelpLink from '../components/HelpLink.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';
import {
  STATES, VALUES, buildTree, errorKeyFor, evidenceRequired, groupFromSearch, historyOf, latestFor,
  looksLikeCommitEvidence, mayRecord, plainText, submitPayload, valueColor,
} from './featureProgress.mjs';

const day = (ms) => (Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString().slice(0, 10) : '');

function StateCell({ t, entry, state, role, onRecord }) {
  const e = entry.cells[state];
  return (
    <TableCell data-state={state} data-fp={entry.fp} sx={{ verticalAlign: 'top', minWidth: 140 }}>
      {e ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          <Chip size="small" color={valueColor(e.value)} label={t(`featureProgress.value.${e.value}`)} />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {e.actorId} · {day(e.atMs)}
          </Typography>
          {e.evidence ? <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{e.evidence}</Typography> : null}
        </Stack>
      ) : <Typography variant="body2" color="text.secondary">{t('featureProgress.none')}</Typography>}
      {mayRecord(role, state) ? (
        <Button size="small" sx={{ mt: 0.5, minWidth: 0 }} data-record={`${entry.fp}|${state}`}
                onClick={() => onRecord(entry.fp, state)}>
          {t('featureProgress.record')}
        </Button>
      ) : null}
    </TableCell>
  );
}

function RecordDialog({ t, group, fp, state, latest, onClose, onDone }) {
  const cur = latestFor(latest, fp, state);
  const values = VALUES[state];
  const [value, setValue] = useState(cur ? cur.value : values[values.length - 1]);
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const payload = submitPayload({ group, fp, state, value, evidence, note, latest });
  const needEvidence = evidenceRequired(value);
  const hint = needEvidence && evidence.trim() && !looksLikeCommitEvidence(evidence);

  const submit = async () => {
    if (!payload.ok) { setErr(t(payload.errorKey)); return; }
    setBusy(true);
    setErr('');
    try {
      await api.recordFeatureState(payload.body);
      onDone();
    } catch (e) {
      const k = errorKeyFor(e);
      setErr(t(k));
      if (k === 'featureProgress.err.stale') onDone({ keepOpen: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('featureProgress.dialog.title', { fp, state: t(`featureProgress.state.${state}`) })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {cur ? t('featureProgress.dialog.chain', { id: cur.id }) : t('featureProgress.dialog.chainFirst')}
          </Typography>
          <TextField select size="small" label={t('featureProgress.dialog.value')} value={value}
                     onChange={(e) => setValue(e.target.value)}>
            {values.map((v) => <MenuItem key={v} value={v}>{t(`featureProgress.value.${v}`)}</MenuItem>)}
          </TextField>
          <TextField size="small" required={needEvidence} id="feature-evidence"
                     label={needEvidence ? t('featureProgress.dialog.evidence') : t('featureProgress.dialog.evidenceOptional')}
                     value={evidence} onChange={(e) => setEvidence(e.target.value)}
                     helperText={hint ? t('featureProgress.dialog.evidenceHint') : ''} />
          <TextField size="small" label={t('featureProgress.dialog.note')} value={note}
                     onChange={(e) => setNote(e.target.value)} />
          {err ? <Alert severity="error">{err}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !payload.ok} data-submit="record">
          {t('featureProgress.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FeatureProgressPage() {
  const t = useT();
  const { search } = useLocation();
  const group = groupFromSearch(search);
  const { status, data, error, reload } = useLoad(() => api.featureStates({ group, rows: true }), [group]);
  const [dialog, setDialog] = useState(null);      // { fp, state }
  const [drawerFp, setDrawerFp] = useState(null);
  const [expanded, setExpanded] = useState({});    // version → bool（缺省全部收起）
  const [openParents, setOpenParents] = useState({});   // 父 fp → bool（S4：子行缺省收起）

  const rows = data?.rows ?? [];
  const events = data?.events ?? [];
  const latest = data?.latest ?? {};
  const role = data?.role ?? '';
  const tree = useMemo(() => buildTree(rows, latest), [rows, latest]);
  const loading = status === 'loading';
  const errKeyTop = status === 'error' ? errorKeyFor(error) : '';

  // 「全部展开」连子行一起开（探针据此数镜像的每个 fp 都在页上）；「全部收起」两级都收。
  const setAll = (open) => {
    setExpanded(Object.fromEntries(tree.map((v) => [v.version, open])));
    setOpenParents(open ? Object.fromEntries(tree.flatMap((v) => v.rows.filter((e) => (e.children || []).length).map((e) => [e.fp, true]))) : {});
  };
  const closeDialog = () => setDialog(null);
  const onDone = (opts) => { reload(); if (!opts?.keepOpen) setDialog(null); };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('featureProgress.title')}
        <HelpLink section="feature-progress" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('featureProgress.subtitle')}</Typography>

      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" color={role ? 'primary' : 'default'} variant={role ? 'filled' : 'outlined'}
              label={role ? t('featureProgress.role', { role }) : t('featureProgress.roleNone')} data-role={role} />
        {status === 'ready' ? (
          <Typography variant="body2" data-summary="rows">
            {t('featureProgress.summary', { rows: rows.length, events: events.length })}
          </Typography>
        ) : null}
        <Button size="small" onClick={() => setAll(true)}>{t('featureProgress.expandAll')}</Button>
        <Button size="small" onClick={() => setAll(false)}>{t('featureProgress.collapseAll')}</Button>
        <Button size="small" onClick={reload}>{t('featureProgress.reload')}</Button>
      </Stack>

      {loading ? <CircularProgress size={24} sx={{ my: 2 }} /> : null}
      {errKeyTop ? <Alert severity="error" sx={{ my: 2 }}>{t(errKeyTop)}</Alert> : null}
      {status === 'ready' && rows.length === 0
        ? <Alert severity="info" sx={{ my: 2 }}>{t('featureProgress.rowsEmpty')}</Alert> : null}

      {tree.map((ver) => (
        <Accordion key={ver.version} expanded={Boolean(expanded[ver.version])}
                   onChange={(_, open) => setExpanded((x) => ({ ...x, [ver.version]: open }))}
                   disableGutters data-version={ver.version}
                   slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary sx={{ '& .MuiAccordionSummary-content': { flexWrap: 'wrap', gap: 1, alignItems: 'center' } }}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>{ver.version}</Typography>
            <Chip size="small" variant="outlined" label={t('featureProgress.sectionSummary', { count: ver.count, parents: ver.parents, children: ver.children })} />
            {STATES.map((s) => (
              <Chip key={s} size="small" variant="outlined"
                    color={ver.progress[s] === ver.progress.total && ver.progress.total > 0 ? 'success' : 'default'}
                    label={`${t(`featureProgress.state.${s}`)} ${t('featureProgress.done', { done: ver.progress[s], total: ver.progress.total })}`} />
            ))}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('featureProgress.col.fp')}</TableCell>
                  <TableCell>{t('featureProgress.col.sentence')}</TableCell>
                  <TableCell>{t('featureProgress.col.source')}</TableCell>
                  <TableCell>{t('featureProgress.col.owner')}</TableCell>
                  {STATES.map((s) => <TableCell key={s}>{t(`featureProgress.state.${s}`)}</TableCell>)}
                  <TableCell>{t('featureProgress.col.history')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ver.rows.flatMap((entry) => {
                  const kids = entry.children || [];
                  const open = Boolean(openParents[entry.fp]);
                  const rowOf = (e, child) => (
                    <TableRow key={e.fp} hover data-child={child ? '1' : undefined} sx={child ? { '& td': { bgcolor: 'action.hover' } } : undefined}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', pl: child ? 4 : undefined }} data-fp-cell={e.fp}>
                        {child ? e.fp.slice(e.fp.indexOf('/') + 1) : e.fp}
                      </TableCell>
                      <TableCell sx={{ minWidth: 140, maxWidth: 260 }}>
                        <Typography variant="body2">{plainText(e.row.sentence)}</Typography>
                        <Typography variant="caption" color="text.secondary">{e.row.status}</Typography>
                        {/* 第三百一十三批（S4）：父自己也是节点、不由子派生——子全实现不等于父实现，父要从父的角度另记。 */}
                        {!child && kids.length ? (
                          <Button size="small" sx={{ display: 'block', minWidth: 0, px: 0.5 }} data-children={e.fp}
                                  onClick={() => setOpenParents((x) => ({ ...x, [e.fp]: !open }))}>
                            {open ? t('featureProgress.childrenClose') : t('featureProgress.childrenOpen', { n: kids.length })}
                          </Button>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ minWidth: 160, maxWidth: 300 }}>
                        <Typography variant="caption">{plainText(e.row.source)}</Typography>
                      </TableCell>
                      <TableCell>{e.row.owner}</TableCell>
                      {STATES.map((s) => (
                        <StateCell key={s} t={t} entry={e} state={s} role={role}
                                   onRecord={(fp, state) => setDialog({ fp, state })} />
                      ))}
                      <TableCell>
                        <Button size="small" sx={{ minWidth: 0 }} data-history={e.fp}
                                onClick={() => setDrawerFp(e.fp)}>{t('featureProgress.col.history')}</Button>
                      </TableCell>
                    </TableRow>
                  );
                  return [rowOf(entry, false), ...(open ? kids.map((k) => rowOf(k, true)) : [])];
                })}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}

      {dialog ? (
        <RecordDialog key={`${dialog.fp}|${dialog.state}|${data?.lastId ?? 0}`} t={t} group={group}
                      fp={dialog.fp} state={dialog.state} latest={latest}
                      onClose={closeDialog} onDone={onDone} />
      ) : null}

      <Drawer anchor="right" open={Boolean(drawerFp)} onClose={() => setDrawerFp(null)}
              PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2 } }}>
        <Typography variant="h6" gutterBottom>{t('featureProgress.history.title', { fp: drawerFp ?? '' })}</Typography>
        <Divider sx={{ mb: 1 }} />
        {drawerFp && historyOf(events, drawerFp).length === 0
          ? <Typography variant="body2" color="text.secondary">{t('featureProgress.history.empty')}</Typography> : null}
        <Stack spacing={1.5}>
          {drawerFp ? historyOf(events, drawerFp).map((e) => (
            <Box key={e.id} data-event={e.id}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>#{e.id}</Typography>
                <Chip size="small" variant="outlined" label={t(`featureProgress.state.${e.state}`)} />
                <Chip size="small" color={valueColor(e.value)} label={t(`featureProgress.value.${e.value}`)} />
                {e.supersedes ? <Typography variant="caption" color="text.secondary">← #{e.supersedes}</Typography> : null}
                <Typography variant="caption" color="text.secondary">{e.source}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">{e.actorId} ({e.actorRole}) · {day(e.atMs)}</Typography>
              {e.evidence ? <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{e.evidence}</Typography> : null}
              {e.note ? <Typography variant="body2" color="text.secondary">{e.note}</Typography> : null}
            </Box>
          )) : null}
        </Stack>
        <Button sx={{ mt: 2, alignSelf: 'flex-start' }} onClick={() => setDrawerFp(null)}>{t('common.close')}</Button>
      </Drawer>
    </Box>
  );
}
